/** BMS SMS message types per bms-api.bab.sa specification. */
export type BmsMessageType = 1 | 2 | 3 | 4;

export const BMS_SMS_LIMITS: Record<BmsMessageType, number> = {
  1: 160, // English
  2: 280, // Unicode
  3: 160, // Special characters
  4: 70, // Arabic
};

const ARABIC_REGEX = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
const SPECIAL_CHAR_REGEX = /[@$[\]{}|\\~^]/;

function hasNonAscii(text: string): boolean {
  for (const char of text) {
    if ((char.codePointAt(0) ?? 0) > 127) return true;
  }
  return false;
}
const RESTRICTED_CHAR_REGEX = /[&#]/;

const DOUBLE_COUNT_CHARS = new Set(['[', ']', '{', '}', '|', '\\', '~', '^']);

export type BmsSmsEncodingInfo = {
  type: BmsMessageType;
  maxLength: number;
  /** Length after applying BMS double-count rules for `[ ] { } | \ ~ ^`. */
  effectiveLength: number;
  charLength: number;
  hasRestrictedChars: boolean;
  isAtLimit: boolean;
};

export function isBmsSmsProvider(provider?: string | null): boolean {
  const normalized = provider?.trim().toLowerCase();
  return normalized === 'bms' || normalized === 'bms_api';
}

/** Infer BMS `type` parameter from message content. */
export function detectBmsMessageType(text: string): BmsMessageType {
  if (ARABIC_REGEX.test(text)) return 4;
  if (hasNonAscii(text)) return 2;
  if (SPECIAL_CHAR_REGEX.test(text)) return 3;
  return 1;
}

export function getBmsSmsMaxLength(text: string): number {
  return BMS_SMS_LIMITS[detectBmsMessageType(text)];
}

export function getBmsSmsEffectiveLength(text: string): number {
  let length = 0;
  for (const char of text) {
    length += DOUBLE_COUNT_CHARS.has(char) ? 2 : 1;
  }
  return length;
}

export function hasBmsRestrictedCharacters(text: string): boolean {
  return RESTRICTED_CHAR_REGEX.test(text);
}

/** Truncate to the effective length allowed for the detected message type. */
export function truncateBmsSms(text: string): string {
  const maxLength = getBmsSmsMaxLength(text);
  let result = '';
  let effective = 0;

  for (const char of text) {
    const weight = DOUBLE_COUNT_CHARS.has(char) ? 2 : 1;
    if (effective + weight > maxLength) break;
    effective += weight;
    result += char;
  }

  return result;
}

type TemplateComponent = { type?: string; text?: string };

/** Resolve BODY text with {{n}} placeholders replaced for campaign/template preview. */
export function resolveTemplateBodyText(
  components: TemplateComponent[] = [],
  variables: Record<number, string> = {}
): string {
  const body = components.find((c) => c.type === 'BODY');
  const sourceText = body?.text ?? components.find((c) => c.text)?.text ?? '';

  return sourceText.replace(/\{\{(\d+)\}\}/g, (_, key) => {
    const index = Number(key) - 1;
    const value = variables[index];
    return value !== undefined ? value : `{{${key}}}`;
  });
}

export function getBmsSmsEncodingInfo(text: string): BmsSmsEncodingInfo {
  const type = detectBmsMessageType(text);
  const maxLength = BMS_SMS_LIMITS[type];
  const effectiveLength = getBmsSmsEffectiveLength(text);

  return {
    type,
    maxLength,
    effectiveLength,
    charLength: text.length,
    hasRestrictedChars: hasBmsRestrictedCharacters(text),
    isAtLimit: effectiveLength >= maxLength,
  };
}
